import classNames from 'classnames';
import { useCallback, useMemo } from 'react';
import { Button, Heading } from 'yoso';
import { DonBalance } from '~/components/Balance';
import { useBalance } from '~/components/Balance/Balance.hooks';
import { myCitizenItemsUrl } from '~/components/Dosi/Dosi.hooks';
import { useEmbeddedViewer } from '~/components/Embedded/Embedded.hooks';
import { useEvent } from '~/components/Event/CheckIn/CheckIn.hooks';
import { T } from '~/components/I18n/Translation';
import { DonIcon } from '~/components/Icon/Don';
import { DescriptionList } from '~/components/List/Description/DescriptionList';
import { useNftCount } from '~/components/Membership/Membership.hooks';
import { useMissionGroup } from '~/components/Mission/Mission.hooks';
import { Link, LinkProps } from '~/components/Route/Link';
import { StatusBarAccordion } from '~/components/Status/Bar/Accordion/StatusBarAccordion';
import { StatusBarProfile } from '~/components/Status/Bar/Profile/StatusBarProfile';
import statusBarStyles from '~/components/Status/Bar/StatusBar.module.scss';
import myItemsStyles from '~/components/Status/Bar/StatusBarMyItems.module.scss';
import { abbreviate, format } from '~/components/Util/number';
import styles from './MissionStatusBar.module.scss';

export type MissionStatusBarProps = {
  className?: string;
};

export const MissionStatusBar = ({ className }: MissionStatusBarProps) => {
  const { data: { amount = 0 } = {} } = useBalance();
  const {
    data: { complete },
  } = useMissionGroup();
  const totalMissionReward = useMemo(
    () => complete.reduce((acc, each) => acc + each.rewardAmount, 0),
    [complete]
  );

  const { data } = useNftCount();
  const { citizen, additional } = data;

  const { open: openViewer } = useEmbeddedViewer();

  const onItemClick = useCallback(() => {
    openViewer(myCitizenItemsUrl);
  }, [openViewer]);

  const { data: checkInEvent } = useEvent('CHECK_IN');
  const { isJoinable } = checkInEvent || {};

  return (
    <div className={classNames(statusBarStyles.root, className)}>
      <StatusBarProfile className={statusBarStyles.header}>
        <div className={styles.reward}>
          <T name="citizen_mission_my_reward_desc" /> <DonIcon className={styles.donIcon} />
          <strong>
            {format(totalMissionReward, {
              sign: true,
              zeroAsEmpty: true,
            })}
          </strong>
        </div>
      </StatusBarProfile>
      <div className={statusBarStyles.body}>
        <StatusBarAccordion>
          <Heading level={5} className={myItemsStyles.title}>
            <T name="citizen_land_menu_myhistory_asset_desc" />
          </Heading>
          <DescriptionList layout="responsive" className={myItemsStyles.list}>
            <dt onClick={onItemClick}>Citizen</dt>
            <dd onClick={onItemClick}>{format(citizen?.count, { zeroAsEmpty: true })}</dd>
            <dt onClick={onItemClick}>Friends</dt>
            <dd onClick={onItemClick}>{format(additional?.friendsCount, { zeroAsEmpty: true })}</dd>
            <dt>
              <Link to="/membership/don/history">DON</Link>
            </dt>
            <dd>
              <Link to="/membership/don/history">
                {amount === 0 ? '-' : <DonBalance format={abbreviate} />}
              </Link>
            </dd>
          </DescriptionList>
        </StatusBarAccordion>
      </div>
      <div className={statusBarStyles.aside}>
        <Button<LinkProps>
          as={Link}
          to="?modal=checkIn"
          className={statusBarStyles.button}
          disabled={!isJoinable}
          data-event="click"
          data-event-target="check_in_mission">
          <T name="citizen_giftbox_daily_checkin_buttontitle" />
        </Button>
      </div>
    </div>
  );
};
